import {
  Button,
  FormControl,
  MenuItem,
  Stack,
  SelectChangeEvent,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useI18n } from "../../../hooks/useI18n";
import {
  CreateDocumentBodyDocumentCategoryEnum,
  DocumentCategoryEnum,
  Document,
  UpdateDocumentBodyDocument,
} from "@syadem/kairos-citizen-js";
import { Dayjs } from "dayjs";
import { dayjs, localizedPlaceholder } from "../../../../utils/dayjs";
import { LightStyledSelect } from "../../../components/mui/LightStyledSelect";
import { LightStyledInput } from "../../../components/mui/LightStyledInput";
import LightStyledDatepicker from "../../../components/mui/LightStyledDatepicker";
import { useServiceBus } from "../../../hooks/useServiceBus";
import { FileUploadZone } from "./FileUploadZone";
import { FileAttachment } from "./components/FileAttachment";

interface DocumentFormProps {
  onClose: () => void;
  existingDocument?: Document;
  onDeleteAttachment?: (fileId: string) => void;
  onDownloadAttachment?: (documentId: string, attachment: { fileId: string; filename: string }) => void;
  onPreviewAttachment?: (documentId: string, attachment: { fileId: string; filename: string }) => void;
  isDeletingAttachment?: { fileId: string } | null;
  documents: Document[];
}

const MAX_DATE = dayjs.utc();
const MIN_DATE = dayjs.utc().subtract(122, "year");

export function DocumentForm({
  onClose,
  existingDocument,
  documents,
  onDeleteAttachment,
  onDownloadAttachment,
  onPreviewAttachment,
  isDeletingAttachment
}: DocumentFormProps) {
  const { id } = useParams() as { id: string };
  const { t, locale } = useI18n();
  const theme = useTheme();
  const [isSending, setIsSending] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});
  const serviceBus = useServiceBus();

  const formatDefaultDocumentName = useCallback(
    (category: DocumentCategoryEnum) => {
      return `${dayjs().format("YYYYMMDD")} - ${t(`documents.attributes.categories.${category}`)}`;
    },
    [t],
  );

  const isVaccinationBookCategoryAllowed = useMemo(() => {
    const alreadyHasVaccinationBookDocument = documents?.some(
      (doc) => doc.category === CreateDocumentBodyDocumentCategoryEnum.VaccinationBook,
    );
    const isEditingVaccinationBookDocument =
      existingDocument?.category === CreateDocumentBodyDocumentCategoryEnum.VaccinationBook;
    return isEditingVaccinationBookDocument || !alreadyHasVaccinationBookDocument;
  }, [documents, existingDocument?.category]);
  const [files, setFiles] = useState<Array<File & { isValid?: boolean }>>([]);
  const [documentCategory, setDocumentCategory] = useState<DocumentCategoryEnum>(
    existingDocument?.category ||
    (isVaccinationBookCategoryAllowed
      ? CreateDocumentBodyDocumentCategoryEnum.VaccinationBook
      : CreateDocumentBodyDocumentCategoryEnum.Letter),
  );
  const [documentName, setDocumentName] = useState(
    existingDocument?.name || formatDefaultDocumentName(documentCategory),
  );
  const [date, setDate] = useState<Dayjs | null>(existingDocument?.date ? dayjs(existingDocument.date) : null);

  const initialDate = existingDocument?.date ? dayjs(existingDocument.date) : null; // track initial date in edit mode

  const hasChanged = useCallback(() => {
    if (!existingDocument) return true;

    return (
      documentName !== existingDocument.name ||
      documentCategory !== existingDocument.category ||
      !((initialDate === null && date === null) || (initialDate && date && initialDate.isSame(date, "day")))
    );
  }, [documentName, documentCategory, date, existingDocument, initialDate]);

  const isFormValid = useCallback(() => {
    const isValidDate = date
      ? date.isValid() &&
      (date.isSame(MIN_DATE) || date.isAfter(MIN_DATE)) &&
      (date.isSame(MAX_DATE) || date.isBefore(MAX_DATE))
      : true;
    const hasRequiredFiles = existingDocument || files.length > 0;
    const hasInvalidFiles = files.some((file) => file.isValid === false);
    return isValidDate && documentName.length > 0 && hasRequiredFiles && !hasInvalidFiles && !fileError;
  }, [date, documentName, files, existingDocument, fileError]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!existingDocument && !files.length) {
      throw new Error("Invalid form");
    }

    try {
      setIsSending(true);
      setUploadProgress({});

      if (existingDocument) {
        const updatedDocument: UpdateDocumentBodyDocument = {};
        if (documentName !== existingDocument.name) {
          updatedDocument.name = documentName;
        }
        if (documentCategory !== existingDocument.category) {
          updatedDocument.category = documentCategory;
        }
        if (!((initialDate === null && date === null) || (initialDate && date && initialDate.isSame(date, "day")))) {
          updatedDocument.date = date ? date.toDate() : null;
        }

        // First update the document metadata
        if (Object.keys(updatedDocument).length > 0) {
          const updateResult = await serviceBus.dispatch({
            type: "updateDocument",
            healthRecordId: id,
            documentId: existingDocument.id,
            document: updatedDocument,
          });

          if (updateResult.type === "error") {
            throw new Error(updateResult.message);
          }
        }

        // Finally handle new file uploads if any
        if (files.length > 0) {
          const validFiles = files.filter((file) => file.isValid !== false);
          const attachResult = await serviceBus.dispatch({
            type: "attachDocumentFiles",
            healthRecordId: id,
            documentId: existingDocument.id,
            attachments: validFiles,
            onProgressUpdate: (fileIndex: number, progress: number) => {
              setUploadProgress((prev) => ({
                ...prev,
                [fileIndex]: progress,
              }));
            },
          });

          if (attachResult.type === "error") {
            throw new Error(attachResult.message);
          }
        }
      } else {
        const validFiles = files.filter((file) => file.isValid !== false);

        const createResult = await serviceBus.dispatch({
          type: "createDocumentAndAttachFiles",
          healthRecordId: id,
          document: {
            name: documentName,
            category: documentCategory,
            date: date ? date.toDate() : undefined,
          },
          attachments: validFiles,
          onProgressUpdate: (fileIndex: number, progress: number) => {
            setUploadProgress((prev) => ({
              ...prev,
              [fileIndex]: progress,
            }));
          },
        });

        if (createResult.type === "error") {
          throw new Error(createResult.message);
        }
      }

      onClose();
    } catch (error) {
      console.error("Error in document operation:", error);
      if (error instanceof Error) {
        console.error("Error details:", {
          message: error.message,
          stack: error.stack,
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSelectCategory = (e: SelectChangeEvent<string>) => {
    const newCategory = e.target.value as DocumentCategoryEnum;
    setDocumentCategory(newCategory);

    // Only update name when creating a new document and the user hasn't manually changed the name
    if (!existingDocument && documentName === formatDefaultDocumentName(documentCategory)) {
      setDocumentName(formatDefaultDocumentName(newCategory));
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 0, sm: 1 },
          pb: 2,
        }}
      >
        <Stack spacing={2} sx={{ minWidth: { xs: "100%", md: 350 } }}>
          <FormControl fullWidth size="small" required>
            <LightStyledSelect
              label={t("documents.attributes.category")}
              name="category"
              value={documentCategory}
              renderValue={(category) => t(`documents.attributes.categories.${category}`)}
              required
              onChange={handleSelectCategory}
            >
              {Object.values(CreateDocumentBodyDocumentCategoryEnum)
                .filter((category) =>
                  isVaccinationBookCategoryAllowed
                    ? true
                    : category !== CreateDocumentBodyDocumentCategoryEnum.VaccinationBook,
                )
                .map((category) => (
                  <MenuItem key={category} value={category}>
                    {t(`documents.attributes.categories.${category}`)}
                  </MenuItem>
                ))}
            </LightStyledSelect>
          </FormControl>

          <LightStyledInput
            label={t("documents.attributes.name")}
            name="name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            required
          />

          <LightStyledDatepicker
            fullWidth
            label={t("documents.attributes.date")}
            maxDate={MAX_DATE}
            minDate={MIN_DATE}
            name="date"
            value={date}
            placeholder={localizedPlaceholder(locale, t)}
            onChange={(date) => setDate(date ? date : null)}
          />

          {existingDocument && existingDocument.attachments.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                {t("documents.attributes.attachments")}
              </Typography>
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                  margin: 0,
                  padding: 0
                }}
              >
                {existingDocument.attachments.map((attachment) => (
                  <FileAttachment
                    key={attachment.fileId}
                    fileName={attachment.filename}
                    fileId={attachment.fileId}
                    onDelete={onDeleteAttachment}
                    onDownload={fileId =>
                      onDownloadAttachment?.(existingDocument.id, {
                        fileId,
                        filename: existingDocument.attachments.find(a => a.fileId === fileId)?.filename || ''
                      })}
                    onPreview={fileId =>
                      onPreviewAttachment?.(existingDocument.id, {
                        fileId,
                        filename: existingDocument.attachments.find(a => a.fileId === fileId)?.filename || ''
                      })}
                    loading={isDeletingAttachment?.fileId === attachment.fileId}
                  />
                ))}
              </Box>
            </Box>
          )}

          <FileUploadZone
            files={files}
            setFiles={setFiles}
            fileError={fileError}
            setFileError={setFileError}
            loading={isSending}
            uploadProgress={uploadProgress}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          px: 1,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={isSending}>
            {t("common.cta.cancel")}
          </Button>
          <Button
            variant="contained"
            type="submit"
            loading={isSending}
            disabled={
              isSending ||
              !isFormValid() ||
              (existingDocument && !hasChanged() && files.length === 0)
            }
          >
            {t("common.cta.save")}
          </Button>
        </Stack>
      </Box>
    </form>
  );
}
